import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../integrations/supabase/client';
import { useToast } from '../../hooks/use-toast';
import { Button } from '../ui/button';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import Image from '@tiptap/extension-image';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableHeader } from '@tiptap/extension-table-header';
import { TableCell } from '@tiptap/extension-table-cell';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  Heading1, Heading2, Heading3, List, ListOrdered,
  AlignLeft, AlignCenter, AlignRight, AlignJustify,
  Minus, ImagePlus, Code, Save, Eye, Loader2, Columns2, Columns3
} from 'lucide-react';

const ConfiguracaoCabecalhoContrato: React.FC = () => {
  useAuth(); // Required for authentication
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [configId, setConfigId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Variáveis disponíveis
  const variaveis = [
    { chave: 'data_atual', descricao: 'Data atual' },
    { chave: 'titulo_processo', descricao: 'Título do processo' },
    { chave: 'tipo_processo', descricao: 'Tipo do processo (venda/locação)' },
    { chave: 'unidade', descricao: 'Unidade do imóvel' },
    { chave: 'comprador_nome', descricao: 'Nome do comprador' },
    { chave: 'vendedor_nome', descricao: 'Nome do vendedor' },
    { chave: 'locatario_nome', descricao: 'Nome do locatário' },
    { chave: 'locador_nome', descricao: 'Nome do locador' },
    { chave: 'imovel_endereco', descricao: 'Endereço do imóvel' },
    { chave: 'imovel_tipo', descricao: 'Tipo do imóvel' },
    { chave: 'valor_venda', descricao: 'Valor de venda' },
    { chave: 'valor_locacao', descricao: 'Valor de locação' },
  ];

  const editor = useEditor({
    extensions: [
      StarterKit,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Underline,
      // Tabelas para permitir colunas lado a lado
      Table.configure({
        resizable: false,
        HTMLAttributes: {
          style: 'width:100%; border-collapse:collapse; margin-bottom:20px;'
        }
      }),
      TableRow,
      TableHeader,
      TableCell.configure({
        HTMLAttributes: {
          style: 'vertical-align:middle; padding: 0 10px;'
        }
      }),
      Image.configure({
        inline: true,
        allowBase64: true,
        HTMLAttributes: {
          class: 'logo-header',
        },
      }),
    ],
    content: '',
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none min-h-[300px] focus:outline-none',
      },
    },
    onUpdate: () => {
      // Editor atualizado
    },
  });

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('configuracoes_contratos')
        .select('*')
        .eq('ativo', true)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setConfigId(data.id);
        editor?.commands.setContent(data.cabecalho_html || '');
      }
    } catch (error) {
      console.error('Erro ao carregar configuração:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar a configuração do cabeçalho",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!editor) return;

    try {
      setSaving(true);
      const cabecalhoHtml = editor.getHTML();

      if (configId) {
        // Atualizar existente
        const { error } = await supabase
          .from('configuracoes_contratos')
          .update({
            cabecalho_html: cabecalhoHtml,
            updated_at: new Date().toISOString(),
          })
          .eq('id', configId);

        if (error) throw error;
      } else {
        // Criar novo
        const { data, error } = await supabase
          .from('configuracoes_contratos')
          .insert({
            cabecalho_html: cabecalhoHtml,
            ativo: true,
          })
          .select()
          .single();

        if (error) throw error;
        setConfigId(data.id);
      }

      toast({
        title: "Sucesso",
        description: "Cabeçalho salvo com sucesso! Será incluído automaticamente em todos os contratos.",
      });
    } catch (error) {
      console.error('Erro ao salvar configuração:', error);
      toast({
        title: "Erro",
        description: "Não foi possível salvar o cabeçalho",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar tipo de arquivo
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Erro",
        description: "Por favor, selecione um arquivo de imagem",
        variant: "destructive",
      });
      return;
    }

    // Validar tamanho (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "Erro",
        description: "A imagem deve ter no máximo 5MB",
        variant: "destructive",
      });
      return;
    }

    try {
      setUploading(true);

      // Upload para Supabase Storage
      const fileName = `${Date.now()}-${file.name}`;
      const { data, error } = await supabase.storage
        .from('contratos-logos')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (error) throw error;

      // Obter URL pública
      const { data: { publicUrl } } = supabase.storage
        .from('contratos-logos')
        .getPublicUrl(data.path);

      // Inserir imagem no editor
      editor?.chain().focus().setImage({ src: publicUrl }).run();

      toast({
        title: "Sucesso",
        description: "Logo inserido com sucesso!",
      });
    } catch (error) {
      console.error('Erro ao fazer upload da imagem:', error);
      toast({
        title: "Erro",
        description: "Não foi possível fazer upload da imagem",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const insertVariable = (chave: string) => {
    if (!editor) return;
    editor.chain().focus().insertContent(`{{${chave}}}`).run();
  };

  const insertFlexLayout = (columns: number) => {
    if (!editor) return;

    const makeCell = (text: string) => ({
      type: 'tableCell',
      content: [
        {
          type: 'paragraph',
          content: [
            { type: 'text', text }
          ]
        }
      ]
    });

    const cells = Array.from({ length: columns }).map((_, i) => makeCell(`Coluna ${i + 1}`));

    editor
      .chain()
      .focus()
      .insertContent({
        type: 'table',
        content: [
          {
            type: 'tableRow',
            content: cells,
          },
        ],
      })
      .run();

    toast({
      title: 'Layout inserido',
      description: `Layout com ${columns} colunas adicionado. Você pode editar o texto e inserir imagens dentro das colunas.`,
    });
  };

  const getPreviewHtml = () => {
    if (!editor) return '';
    
    let html = editor.getHTML();
    
    // Substituir variáveis por valores de exemplo
    const exemplos: Record<string, string> = {
      data_atual: new Date().toLocaleDateString('pt-BR'),
      titulo_processo: 'Venda de Apartamento',
      tipo_processo: 'Venda',
      unidade: '102 - Bloco A',
      comprador_nome: 'João da Silva',
      vendedor_nome: 'Maria Souza',
      imovel_endereco: 'Rua das Flores, 123',
      imovel_tipo: 'Apartamento',
      valor_venda: 'R$ 500.000,00',
    };

    Object.entries(exemplos).forEach(([chave, valor]) => {
      const regex = new RegExp(`{{${chave}}}`, 'g');
      html = html.replace(regex, valor);
    });

    return html;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!editor) {
    return null;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Cabeçalho de Contratos</h2>
          <p className="text-muted-foreground mt-1">
            Configure o cabeçalho que será incluído automaticamente em todos os contratos
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowPreview(!showPreview)}
          >
            <Eye className="h-4 w-4 mr-2" />
            {showPreview ? 'Ocultar' : 'Preview'}
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Salvar
          </Button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-card border rounded-lg p-4">
        <div className="flex flex-wrap gap-2">
          {/* Formatação de texto */}
          <div className="flex gap-1 border-r pr-2">
            <Button
              variant={editor.isActive('bold') ? 'default' : 'ghost'}
              size="sm"
              onClick={() => editor.chain().focus().toggleBold().run()}
            >
              <Bold className="h-4 w-4" />
            </Button>
            <Button
              variant={editor.isActive('italic') ? 'default' : 'ghost'}
              size="sm"
              onClick={() => editor.chain().focus().toggleItalic().run()}
            >
              <Italic className="h-4 w-4" />
            </Button>
            <Button
              variant={editor.isActive('underline') ? 'default' : 'ghost'}
              size="sm"
              onClick={() => editor.chain().focus().toggleUnderline().run()}
            >
              <UnderlineIcon className="h-4 w-4" />
            </Button>
            <Button
              variant={editor.isActive('strike') ? 'default' : 'ghost'}
              size="sm"
              onClick={() => editor.chain().focus().toggleStrike().run()}
            >
              <Strikethrough className="h-4 w-4" />
            </Button>
          </div>

          {/* Títulos */}
          <div className="flex gap-1 border-r pr-2">
            <Button
              variant={editor.isActive('heading', { level: 1 }) ? 'default' : 'ghost'}
              size="sm"
              onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            >
              <Heading1 className="h-4 w-4" />
            </Button>
            <Button
              variant={editor.isActive('heading', { level: 2 }) ? 'default' : 'ghost'}
              size="sm"
              onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            >
              <Heading2 className="h-4 w-4" />
            </Button>
            <Button
              variant={editor.isActive('heading', { level: 3 }) ? 'default' : 'ghost'}
              size="sm"
              onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            >
              <Heading3 className="h-4 w-4" />
            </Button>
          </div>

          {/* Listas */}
          <div className="flex gap-1 border-r pr-2">
            <Button
              variant={editor.isActive('bulletList') ? 'default' : 'ghost'}
              size="sm"
              onClick={() => editor.chain().focus().toggleBulletList().run()}
            >
              <List className="h-4 w-4" />
            </Button>
            <Button
              variant={editor.isActive('orderedList') ? 'default' : 'ghost'}
              size="sm"
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
            >
              <ListOrdered className="h-4 w-4" />
            </Button>
          </div>

          {/* Alinhamento */}
          <div className="flex gap-1 border-r pr-2">
            <Button
              variant={editor.isActive({ textAlign: 'left' }) ? 'default' : 'ghost'}
              size="sm"
              onClick={() => editor.chain().focus().setTextAlign('left').run()}
            >
              <AlignLeft className="h-4 w-4" />
            </Button>
            <Button
              variant={editor.isActive({ textAlign: 'center' }) ? 'default' : 'ghost'}
              size="sm"
              onClick={() => editor.chain().focus().setTextAlign('center').run()}
            >
              <AlignCenter className="h-4 w-4" />
            </Button>
            <Button
              variant={editor.isActive({ textAlign: 'right' }) ? 'default' : 'ghost'}
              size="sm"
              onClick={() => editor.chain().focus().setTextAlign('right').run()}
            >
              <AlignRight className="h-4 w-4" />
            </Button>
            <Button
              variant={editor.isActive({ textAlign: 'justify' }) ? 'default' : 'ghost'}
              size="sm"
              onClick={() => editor.chain().focus().setTextAlign('justify').run()}
            >
              <AlignJustify className="h-4 w-4" />
            </Button>
          </div>

          {/* Upload de Logo */}
          <div className="flex gap-1 border-r pr-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ImagePlus className="h-4 w-4" />
              )}
            </Button>
          </div>

          {/* Layouts Flexbox */}
          <div className="flex gap-1 border-r pr-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => insertFlexLayout(2)}
              title="Inserir layout de 2 colunas (Logo + Texto)"
            >
              <Columns2 className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => insertFlexLayout(3)}
              title="Inserir layout de 3 colunas"
            >
              <Columns3 className="h-4 w-4" />
            </Button>
          </div>

          {/* Linha horizontal */}
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => editor.chain().focus().setHorizontalRule().run()}
            >
              <Minus className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Variáveis */}
        <div className="mt-4 pt-4 border-t">
          <p className="text-sm font-medium mb-2">Inserir Variáveis:</p>
          <div className="flex flex-wrap gap-2">
            {variaveis.map((v) => (
              <Button
                key={v.chave}
                variant="outline"
                size="sm"
                onClick={() => insertVariable(v.chave)}
                title={v.descricao}
              >
                <Code className="h-3 w-3 mr-1" />
                {`{{${v.chave}}}`}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Editor */}
      <div className="bg-card border rounded-lg p-6">
        <EditorContent
          editor={editor}
          className="prose prose-sm max-w-none min-h-[300px] focus:outline-none"
        />
      </div>

      {/* Preview */}
      {showPreview && (
        <div className="bg-card border rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4">Preview (com variáveis substituídas)</h3>
          <div
            className="prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: getPreviewHtml() }}
          />
        </div>
      )}

      {/* Informações */}
      <div className="bg-muted/50 border rounded-lg p-4">
        <h4 className="font-semibold mb-2">ℹ️ Como usar:</h4>
        <ul className="text-sm space-y-1 text-muted-foreground">
          <li>• Use os botões <Columns2 className="inline h-3 w-3" /> ou <Columns3 className="inline h-3 w-3" /> para inserir layouts de colunas</li>
          <li>• Dentro do layout, você pode adicionar imagens e texto na mesma linha</li>
          <li>• Clique no botão <ImagePlus className="inline h-3 w-3" /> para fazer upload do logo da empresa</li>
          <li>• Substitua os textos "Logo aqui" e "Texto aqui" pelo seu conteúdo</li>
          <li>• Use os botões de formatação para estilizar o texto</li>
          <li>• Insira variáveis que serão substituídas automaticamente em cada contrato</li>
          <li>• O cabeçalho configurado será automaticamente incluído no início de todos os contratos gerados</li>
          <li>• Use o Preview para ver como ficará com dados reais</li>
        </ul>
      </div>
    </div>
  );
};

export default ConfiguracaoCabecalhoContrato;
